/**
 * Tipos para el sistema de indexación de superficies (atlas tiles)
 * 
 * Las superficies son tiles extraídos de archivos atlas (PNG grandes con múltiples tiles).
 * Este sistema permite definir múltiples superficies desde un solo archivo de imagen,
 * similar a cómo funciona Tiled o herramientas similares.
 */

/**
 * Definición de una superficie individual extraída de un atlas.
 * Similar a las decoraciones pero con información adicional del atlas.
 */
export interface SurfaceDefinition {
  /** Nombre para mostrar en el editor (ej: "Camino de hierba 1") */
  name: string;
  
  /** Tipo de superficie (ej: "path", "dungeon_banner", "floor") */
  type: string;
  
  /** Subtipo opcional para categorización más específica (ej: "grass", "stone") */
  subtype?: string;
  
  /** Nombre del archivo atlas PNG (ej: "paths_atlas.png") */
  atlasFile: string;
  
  /** Coordenada X en el grid del atlas (0-based) */
  gridX: number;
  
  /** Coordenada Y en el grid del atlas (0-based) */
  gridY: number;
  
  /** Número de tiles horizontales (ancho). Default: 1 */
  tilesX?: number;
  
  /** Número de tiles verticales (alto). Default: 1 */
  tilesY?: number;
}

/**
 * Índice de superficies.
 * Las claves son IDs únicos de superficie (ej: "path_grass_1")
 */
export type SurfaceIndex = Record<string, SurfaceDefinition>;

/**
 * Estructura completa del assets-index.json
 */
export interface AssetsIndex {
  decorations?: Record<string, { name: string; type: string }>;
  animations?: Record<string, {
    name: string;
    type: string;
    columns: number;
    rows: number;
    skipLast?: number;
    offsetY?: number;
  }>;
  surfaces?: SurfaceIndex;
}

/**
 * Información de un atlas cargado
 */
export interface AtlasInfo {
  /** Nombre del archivo */
  fileName: string;
  
  /** Ancho del atlas en píxeles */
  width: number;
  
  /** Alto del atlas en píxeles */
  height: number;
  
  /** Tamaño de cada tile (normalmente TILE_SIZE) */
  tileSize: number;
  
  /** Número de columnas en el grid */
  columns: number;
  
  /** Número de filas en el grid */
  rows: number;
}

/**
 * Genera la clave de sprite para una superficie.
 * El formato es: surface_{atlasFile}_{gridX}_{gridY}
 * o simplemente el ID de la superficie.
 */
export function getSurfaceSpriteKey(surfaceId: string): string {
  return `surface_${surfaceId}`;
}

/**
 * Genera la clave de textura para un atlas.
 * El formato es: atlas_{fileName}
 */
export function getAtlasTextureKey(atlasFileName: string): string {
  // Remover extensión y caracteres especiales
  const baseName = atlasFileName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
  return `atlas_${baseName}`;
}

/**
 * Calcula la posición en píxeles de un tile en el atlas.
 */
export function getAtlasTilePosition(
  gridX: number,
  gridY: number,
  tileSize: number
): { x: number; y: number } {
  return {
    x: gridX * tileSize,
    y: gridY * tileSize,
  };
}

/**
 * Valida que una definición de superficie sea correcta.
 */
export function validateSurfaceDefinition(surface: SurfaceDefinition): string[] {
  const errors: string[] = [];
  
  if (!surface.name || surface.name.trim() === '') {
    errors.push('El nombre es requerido');
  }
  
  if (!surface.type || surface.type.trim() === '') {
    errors.push('El tipo es requerido');
  }
  
  if (!surface.atlasFile || surface.atlasFile.trim() === '') {
    errors.push('El archivo atlas es requerido');
  }
  
  if (typeof surface.gridX !== 'number' || surface.gridX < 0) {
    errors.push('gridX debe ser un número >= 0');
  }
  
  if (typeof surface.gridY !== 'number' || surface.gridY < 0) {
    errors.push('gridY debe ser un número >= 0');
  }
  
  return errors;
}

/**
 * Obtiene todos los archivos atlas únicos de un índice de superficies.
 */
export function getUniqueAtlasFiles(surfaces: SurfaceIndex): string[] {
  const atlasFiles = new Set<string>();
  
  for (const surface of Object.values(surfaces)) {
    if (surface.atlasFile) {
      atlasFiles.add(surface.atlasFile);
    }
  }
  
  return Array.from(atlasFiles);
}

/**
 * Agrupa superficies por tipo.
 */
export function groupSurfacesByType(surfaces: SurfaceIndex): Record<string, Array<{ id: string; surface: SurfaceDefinition }>> {
  const grouped: Record<string, Array<{ id: string; surface: SurfaceDefinition }>> = {};
  
  for (const [id, surface] of Object.entries(surfaces)) {
    if (!grouped[surface.type]) {
      grouped[surface.type] = [];
    }
    grouped[surface.type].push({ id, surface });
  }
  
  return grouped;
}

/**
 * Agrupa superficies por archivo atlas.
 */
export function groupSurfacesByAtlas(surfaces: SurfaceIndex): Record<string, Array<{ id: string; surface: SurfaceDefinition }>> {
  const grouped: Record<string, Array<{ id: string; surface: SurfaceDefinition }>> = {};
  
  for (const [id, surface] of Object.entries(surfaces)) {
    if (!grouped[surface.atlasFile]) {
      grouped[surface.atlasFile] = [];
    }
    grouped[surface.atlasFile].push({ id, surface });
  }
  
  return grouped;
}
